import React, { FC, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
} from "reactstrap";

import CustomerPaymentDetailModal from "./CustomerPaymentDetailModal";
import {
  CustomerAllPaymentRequest,
  CustomerAllPaymentResponse,
} from "./CustomerProfile.constants";
import { UseMutateFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";
import PaymentDeleteModal from "./PaymentDeleteModal";
import CustomerPaymentHistoryModal from "./CustomerPaymentHistoryModal";
import { formatDecimal } from "../../shared/utils/formatText";
import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../shared/actions/Authentication.actions";
import { Permissions } from "../../shared/models/Types";

export interface ProfileTab2Props {
  data: CustomerAllPaymentResponse | undefined;
  mutate: UseMutateFunction<
    CustomerAllPaymentResponse,
    AxiosError<any>,
    CustomerAllPaymentRequest,
    unknown
  >;
}

const Profiletab2: FC<ProfileTab2Props> = ({ data, mutate }) => {
  const isMobile: boolean = /iPhone|iPad|iPod|Android/i.test(
    navigator.userAgent
  );

  const permissionList = useRecoilValue(permissionsSelector);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState<boolean>(false);
  const [modalOptions, setModalOptions] = useState<{
    id?: number;
    payment_type?: string;
  }>({
    id: undefined,
    payment_type: undefined,
  });

  const toggle = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleDelete = () => {
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const toggleHistory = () => {
    setIsHistoryModalOpen(!isHistoryModalOpen);
  };

  const modalOptionsHandler = (paymentId: number, paymentType?: string) => {
    setModalOptions({ id: paymentId, payment_type: paymentType });
  };

  const viewPaymentPermission = permissionList[Permissions.ViewPayment];
  const updatePaymentPermission = permissionList[Permissions.ChangePayment];
  const deletePaymentPermission = permissionList[Permissions.DeletePayment];

  let totalRetrieval = data?.results.reduce(
    (sum: any, payment: any) => sum + payment.retrieval,
    0
  );

  return (
    <React.Fragment>
      <Card>
        <CardHeader>
          <CardTitle className="mb-0">
            <h5 className="font-size-15">
              Geçmiş Ödemeler -{`>`} (Toplam Borç: {`${totalRetrieval} ₺`})
            </h5>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="p-4 border rounded">
            <div className="table-responsive">
              <Table className="table-nowrap align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: "70px" }}>Ödeme/Tür</th>
                    {!isMobile && (
                      <>
                        <th>Not</th>
                        <th>Toplam</th>
                        <th>Ödenen</th>
                        <th>Kalan</th>
                        <th>Oluşturan</th>
                        <th className="text-end" style={{ width: "120px" }}>
                          İşlem
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {viewPaymentPermission &&
                    data?.results.map((payments) => {
                      return (
                        <tr key={payments?.id}>
                          <td>
                            {payments?.name_of_payment}
                            <p className="font-size-13 text-muted mb-0">
                              {payments?.payment_type == "ap"
                                ? "Peşin"
                                : "Taksitli"}
                            </p>

                            {isMobile && (
                              <div style={{ paddingTop: 15 }}>
                                <div className="pb-1">
                                  <Row>
                                    <Col>
                                      <div>
                                        <h5 className="font-size-15">
                                          Toplam:
                                        </h5>
                                      </div>
                                    </Col>

                                    <Col>
                                      <div className="text-muted">
                                        <p className="mb-2">
                                          {formatDecimal(payments?.amount)} ₺
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>

                                <div className="pb-1">
                                  <Row>
                                    <Col>
                                      <div>
                                        <h5 className="font-size-15">
                                          Ödenen:
                                        </h5>
                                      </div>
                                    </Col>

                                    <Col>
                                      <div className="text-muted">
                                        <p className="mb-2">
                                          {formatDecimal(
                                            parseInt(payments?.total_paid)
                                          )}
                                          ₺
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>

                                <div className="pb-2">
                                  <Row>
                                    <Col>
                                      <div>
                                        <h5 className="font-size-15">Kalan:</h5>
                                      </div>
                                    </Col>

                                    <Col>
                                      <div className="text-muted">
                                        <p className="mb-2">
                                          {formatDecimal(
                                            parseInt(payments?.retrieval)
                                          )}{" "}
                                          ₺
                                        </p>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>

                                <span
                                  style={{ marginRight: 6 }}
                                  onClick={() => {
                                    modalOptionsHandler(
                                      payments?.id,
                                      payments.payment_type
                                    );
                                    toggleHistory();
                                  }}
                                  className="badge btn btn-primary rounded-pill font-size-10 p-2 pointer"
                                >
                                  <i className="mdi mdi-border-color noti-icon"></i>{" "}
                                  Geçmiş
                                </span>

                                {updatePaymentPermission && (
                                  <span
                                    onClick={() => {
                                      modalOptionsHandler(payments?.id);
                                      toggle();
                                    }}
                                    style={
                                      parseInt(payments?.retrieval) === 0
                                        ? {
                                            cursor: "not-allowed",
                                            opacity: 0.5,
                                            pointerEvents: "none",
                                          }
                                        : { cursor: "pointer" }
                                    }
                                    className="badge btn btn-success rounded-pill font-size-10 p-2 pointer"
                                  >
                                    <i className="mdi mdi-border-color noti-icon"></i>{" "}
                                    Ödeme Yap
                                  </span>
                                )}

                                {deletePaymentPermission && (
                                  <span
                                    style={{ marginLeft: 9 }}
                                    onClick={() => {
                                      modalOptionsHandler(payments?.id);
                                      toggleDelete();
                                    }}
                                    className="badge btn btn-danger rounded-pill font-size-10 p-2 pointer"
                                  >
                                    <i className="mdi mdi-border-color noti-icon"></i>{" "}
                                    Sil
                                  </span>
                                )}
                              </div>
                            )}
                          </td>

                          {!isMobile && (
                            <>
                              <td>{payments?.note}</td>
                              <td>{formatDecimal(payments?.amount)} ₺</td>
                              <td>
                                {formatDecimal(parseInt(payments?.total_paid))}{" "}
                                ₺
                              </td>
                              <td>
                                <h5 className="font-size-15 mb-1">
                                  {formatDecimal(parseInt(payments?.retrieval))}{" "}
                                  ₺
                                </h5>
                              </td>
                              <td>{payments?.person_who_created.fullname}</td>
                            </>
                          )}

                          {!isMobile && (
                            <td className="text-end">
                              {
                                <span
                                  style={{ marginRight: 6 }}
                                  onClick={() => {
                                    modalOptionsHandler(payments?.id);
                                    toggleHistory();
                                  }}
                                  className="badge btn btn-primary rounded-pill font-size-10 p-2 pointer"
                                >
                                  <i className="mdi mdi-border-color noti-icon"></i>{" "}
                                  Geçmiş
                                </span>
                              }

                              {updatePaymentPermission && (
                                <span
                                  onClick={() => {
                                    modalOptionsHandler(payments?.id);
                                    toggle();
                                  }}
                                  style={
                                    parseInt(payments?.retrieval) === 0
                                      ? {
                                          cursor: "not-allowed",
                                          opacity: 0.5,
                                          pointerEvents: "none",
                                        }
                                      : { cursor: "pointer" }
                                  }
                                  className="badge btn btn-success rounded-pill font-size-10 p-2 pointer"
                                >
                                  <i className="mdi mdi-border-color noti-icon"></i>{" "}
                                  Ödeme Yap
                                </span>
                              )}

                              {deletePaymentPermission && (
                                <span
                                  style={{ marginLeft: 9 }}
                                  onClick={() => {
                                    modalOptionsHandler(payments?.id);
                                    toggleDelete();
                                  }}
                                  className="badge btn btn-danger rounded-pill font-size-10 p-2 pointer"
                                >
                                  <i className="mdi mdi-border-color noti-icon"></i>{" "}
                                  Sil
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          </div>
        </CardBody>
      </Card>

      {isModalOpen && modalOptions?.id && (
        <CustomerPaymentDetailModal
          mutate={mutate}
          isModalOpen={isModalOpen}
          toggle={toggle}
          modalOptions={modalOptions}
          allPayments={data}
        />
      )}

      {isDeleteModalOpen && modalOptions?.id && (
        <PaymentDeleteModal
          mutate={mutate}
          isModalOpen={isDeleteModalOpen}
          toggle={toggleDelete}
          modalOptions={modalOptions}
        />
      )}

      {isHistoryModalOpen && modalOptions?.id && (
        <CustomerPaymentHistoryModal
          isModalOpen={isHistoryModalOpen}
          toggle={toggleHistory}
          paymentId={modalOptions?.id}
          mutate={mutate}
        />
      )}
    </React.Fragment>
  );
};

export default Profiletab2;
